// Single source of truth for every path PATTERN registered in the
// @lit-labs/router route table (frontend/src/app.ts's `_router`).
//
// Nothing here is derived from app.ts at build time — the Router array is a
// class-field literal built from ~90 inline `{ path, enter, render }`
// entries closing over instance methods (guards, `this._shell`, `this._user`),
// and pulling that apart into a shared, importable structure would touch
// every one of those entries for very little gain. Instead this list is a
// hand-authored MIRROR, and frontend/src/lint/route-table-drift.test.ts is
// what keeps it honest: it source-parses app.ts's actual `path: '...'`
// literals (the same technique lint/popstate-guard-order.test.ts already
// uses to lock an app.ts invariant no behavioural test can see) and asserts
// the two lists are identical. Add a route to app.ts, add it here, or the
// lint test fails the build — drift can't ship silently.
//
// Consumed today by utils/retestTarget.ts, which needs to know whether a
// feedback report's stored page is still a real, reachable page before
// offering it as a re-test link.
import { SUPPORT_EMAIL_PAGE_ENABLED } from './utils/supportEmailSwitch.js';

export const ROUTE_PATTERNS: readonly string[] = [
	"/",
	"/login",
	// Reserve Panels — the unauthenticated device app (pairing + room
	// display). Bare, no shell, same shape as /login.
	"/panels",
	// Roster time-clock tap-to-confirm card — public, bare, tokened
	// (app.ts registered it without this entry; the route-table-drift
	// lint caught the miss).
	"/clock/:token",
	"/inbox",
	"/notifications",
	"/dashboard",
	"/settings",
	"/dashboards/:id",
	"/reports",
	"/report-builder",
	"/report-builder/new",
	"/report-builder/:id",
	// The landing for an emailed scheduled-report link (see the route's
	// note in app.ts for why it lives in the SPA and not the API).
	"/reports/download/:token",
	"/s/:token",
	// The anonymous Broadcast book-carousel signage page — bare, no
	// shell, embedded in a lobby-TV iframe (docs/broadcast-book-
	// carousel-spec.md §2). Same "no session, no chrome" shape as
	// /reports/download/:token above.
	"/broadcast/carousel/:client/:configId",
	"/files",
	"/circulate",
	"/reserve",
	"/reserve/bookings",
	"/reserve/calendar",
	"/reserve/rooms",
	"/schedule",
	"/schedule/new",
	"/schedule/bookings/:id",
	"/schedule/bookings/:id/edit",
	// Stage 4, slice A (categories). Static segments before :id, as the router matches in order.
	"/schedule/settings",
	"/schedule/settings/categories/new",
	"/schedule/settings/categories/:id",
	// Stage 4, slice C (providers).
	"/schedule/settings/categories/:id/providers",
	"/schedule/settings/providers/:id/week",
	// Stage 4, slice B: the Group editor. The static `new` segment is
	// registered before `:id`.
	"/schedule/settings/groups/new",
	"/schedule/settings/groups/:id",
	"/lend",
	"/media",
	"/preserve",
	"/preserve/items/new",
	"/preserve/items/:id",
	"/preserve/items/:id/recording",
	"/preserve/oral-history/:id",
	"/preserve/contributions/:id",
	// P3 — entity hubs + the unlinked-items worklist ("the Orphanarium").
	"/preserve/entities/:tagType/:tagText",
	"/preserve/unlinked",
	"/roster",
	// Outreach: ONE rail entry, two sections (Paul, 2026-08-23; see
	// docs/outreach-spec.md's amended §1.3 note). Visits + Bookmobile
	// live under /outreach/*; the old /bookmobile* paths still register
	// (as client-side redirects; see app.ts) so they stay listed here.
	"/outreach",
	"/outreach/visits",
	"/outreach/visits/:id",
	"/outreach/bookmobile",
	"/outreach/bookmobile/vehicles",
	"/outreach/bookmobile/stops",
	"/outreach/bookmobile/routes",
	"/outreach/bookmobile/trips",
	"/outreach/bookmobile/public-timetable",
	"/bookmobile",
	"/bookmobile/stops",
	"/bookmobile/routes",
	"/measure",
	"/measure/pipeline",
	"/measure/d",
	"/measure/d/*",
	"/thrive",
	"/thrive/*",
	"/community",
	"/communications",
	// Desk (docs/desk-plan.md) — the front-of-house staff surface.
	"/desk",
	// Action Center (docs/action-center-plan.md) — the merged approvals queue.
	"/action-center",
	// Billing (the Maxio/SaaSOptics replacement) — staff-only surfaces.
	// The home first: /billing used to have no route, so the nav landed on the
	// contracts list.
	"/billing",
	"/billing/renewals",
	"/billing/contracts",
	"/billing/contracts/new",
	"/billing/contracts/:id",
	"/billing/contracts/:id/renew",
	"/billing/contracts/:id/amend",
	"/billing/accounts",
	"/billing/accounts/:id",
	"/billing/customers",
	"/billing/customers/:id",
	"/billing/products",
	// Order forms, quotes and renewals — one document, three clause sets.
	"/billing/documents",
	// Billing — the Maxio/SaaSOptics replacement (invoices, the invoice run,
	// the month-end close).
	"/billing/invoices",
	"/billing/invoices/:id",
	"/billing/close",
	"/billing/reports",
	// Cheque intake (migration 000462): the batch list, and one batch at
	// ?batch=<id>. The open batch is a query parameter rather than a
	// path segment because the page pushes and pops it itself.
	"/billing/deposits",
	// Bank activity (migration 000472): what QuickBooks says moved on the
	// bank accounts. The open line, the tab and the filters all ride in
	// the query, which the page pushes and pops itself, so there is one
	// route rather than a path per view.
	"/billing/bank-activity",
	// Where COS and Maxio disagree, and the worklist for the one
	// difference a human can clear.
	"/billing/reconciliation",
	// The editable invoice-send email template (Brief AK), and the payment
	// reminder schedule + templates it hosts as a second section (Brief AO).
	"/billing/invoice-settings",
	"/billing/reminder-settings",
	"/nurture",
	"/nurture/segments",
	"/nurture/emails",
	"/nurture/emails/new/studio",
	"/nurture/emails/:id/studio",
	"/nurture/campaigns",
	"/nurture/campaigns/:id/studio",
	"/nurture/journeys",
	"/nurture/surveys",
	"/nurture/surveys/new",
	"/nurture/surveys/:sid",
	"/nurture/orgs",
	"/nurture/work-queue",
	"/nurture/inbound-email",
	"/nurture/board",
	"/nurture/:id",
	"/broadcast",
	"/attend",
	"/attend/events/new",
	"/attend/events/:id/edit",
	"/collections",
	"/annual-report",
	"/explore",
	"/explore/:tab",
	"/partner-admin",
	"/activities",
	"/activity",
	"/me/implementations",
	"/partners",
	"/partners/map",
	"/partners/:id",
	"/prospects",
	"/prospects/by-partner/:id",
	"/prospects/:fscskey",
	"/mail",
	"/calendar",
	"/calendar/team",
	"/conversations",
	"/chat",
	"/timeoff",
	"/training",
	"/training/:key",
	"/training/:key/attempt/:id",
	"/training/:key/results/:id",
	"/tasks",
	"/contacts",
	"/contacts/:id",
	"/peer-comparison/partner/:id",
	"/peer-comparison/fscskey/:fscskey",
	"/tickets",
	"/tickets/new",
	"/tickets/mine",
	...(SUPPORT_EMAIL_PAGE_ENABLED ? ["/tickets/support-email"] : []),
	"/tickets/:id",
	"/projects",
	"/projects/new",
	"/projects/:id",
	"/deals",
	"/deals/board",
	"/deals/:id",
	"/implementation",
	"/implementation/new",
	"/implementation/:moduleName",
	"/partners/:id/implementation",
	"/partners/:id/implementation/:moduleName",
	"/kb",
	"/kb/new",
	"/kb/:slug/edit",
	"/kb/:slug/versions",
	"/kb/:slug",
	"/admin",
	"/admin/users",
	"/admin/audit",
	"/feedback",
	"/admin/feedback",
	"/admin/activity",
	"/admin/timeoff",
	"/admin/training",
	"/admin/labels",
	"/admin/chat-channels",
	"/admin/module-templates",
	"/admin/kb-article-types",
	"/admin/partner-ils-types",
	"/admin/partner-discovery-layers",
	"/admin/partner-purchase-types",
	"/admin/partner-statuses",
	"/admin/partner-zoom-config-types",
	"/admin/partner-currencies",
	"/admin/partner-consortiums",
	"/admin/partner-module-statuses",
	"/admin/project-phases",
	"/admin/project-templates",
	"/admin/escalation-rules",
	"/admin/derivation-rules",
	"/admin/ticket-templates",
	"/admin/custom-fields",
	"/admin/preserve-limits",
	"/admin/preserve-ia-import",
	"/admin/client-settings",
	"/admin/schema-explorer",
	"/admin/data-console",
	"/admin/pls/upload",
	"/admin/pls/link-suggestions",
	"/admin/integrations",
	"/admin/agents",
	"/admin/mcp-library-tools",
	"/admin/agents/new",
	"/admin/agents/:id",
	"/admin/agent-playbooks",
	"/admin/decision-engines",
	// Workflow definitions and the editor (agent workflows W5).
	"/admin/workflows",
	"/admin/workflows/:id",
	// The GitHub App (agent workflows W4): repositories and Connect GitHub's return.
	"/admin/github",
	"/settings/github",
	"/admin/agent-playbooks/new",
	"/admin/agent-playbooks/:id",
	"/agent-approvals/:id",
	"/agent-schedules",
	"/agent-missions",
	"/agent-missions/:id",
	"/workflow-inbox",
	"/workflow-runs",
	"/workflow-runs/:id",
	"/admin/design-system",
	"/admin/design-settings",
	"/admin/triage-review",
	// DEV-only in app.ts (import.meta.env.DEV tree-shakes the route out of a
	// prod build), but the string literal is unconditionally present in the
	// source app.ts is parsed from — kept here so the drift lint test (which
	// reads that source, not a runtime build) has something to match against.
	"/dev/record-shell",
];

function segmentsOf(path: string): string[] {
	return path.split("/").filter(Boolean);
}

/** True when a concrete `path` (e.g. "/tickets/42") satisfies a route
 *  `pattern` (e.g. "/tickets/:id") — same-length segments, `:param`
 *  segments matching anything, everything else matching literally.
 *
 *  A trailing "*" segment (the Measure corkscrew's `/measure/d/*`, which
 *  the real @lit-labs/router resolves via URLPattern's own tail-wildcard)
 *  matches its own prefix plus ANY number of remaining segments, so a
 *  registered level five deep (`/measure/d/circ/physical/adult-fiction`)
 *  is still a known, reachable route through this mirror. */
export function routePatternMatches(pattern: string, path: string): boolean {
	const patternSegments = segmentsOf(pattern);
	const pathSegments = segmentsOf(path);
	if (patternSegments.at(-1) === "*") {
		const prefix = patternSegments.slice(0, -1);
		if (pathSegments.length < prefix.length) return false;
		return prefix.every((seg, i) => seg.startsWith(":") || seg === pathSegments[i]);
	}
	if (patternSegments.length !== pathSegments.length) return false;
	return patternSegments.every((seg, i) => seg.startsWith(":") || seg === pathSegments[i]);
}

/** The first registered pattern `path` satisfies, or null. */
export function matchingRoutePattern(path: string): string | null {
	return ROUTE_PATTERNS.find((pattern) => routePatternMatches(pattern, path)) ?? null;
}

/** True when `path` is reachable through some registered route pattern. */
export function isKnownRoute(path: string): boolean {
	return matchingRoutePattern(path) !== null;
}

/** True when `pattern` carries at least one `:param` segment (e.g.
 *  "/tickets/:id") OR a trailing "*" (e.g. "/measure/d/*", whose captured
 *  tail is itself the resource's identity — a drill level) — i.e. the
 *  route's own path already encodes a record's identity, as opposed to a
 *  route like "/tickets" whose identity (if any) can only come from its
 *  query string. */
export function routePatternHasParam(pattern: string): boolean {
	const segments = segmentsOf(pattern);
	return segments.some((seg) => seg.startsWith(":")) || segments.at(-1) === "*";
}
